/*
 * Retorno do login Google — rota SPA /auth/google.
 *
 * O worker redireciona pra cá com o resultado no FRAGMENT da URL
 * (#token=<jwt> ou #error=<code>) — fragments nunca chegam ao servidor,
 * então o JWT não vaza em access logs. Este módulo processa o fragment
 * ANTES do App montar (chamado pelo main.tsx), persiste o JWT com o MESMO
 * mecanismo do useAuth (localStorage 'drpera-jwt' via storeJwt) e limpa a
 * URL com history.replaceState (token fora do histórico do navegador).
 */
import { storeJwt } from '../api/client';

/** Chave one-shot do erro em sessionStorage (consumida pelo App no mount) */
const GOOGLE_AUTH_ERROR_KEY = 'drpera-google-auth-error';

/** true se o pathname é a rota de retorno do Google */
export function isGoogleAuthReturnPath(pathname: string): boolean {
  return pathname === '/auth/google';
}

/**
 * Processa o fragment do retorno e limpa a URL (volta pra raiz).
 * - #token= → storeJwt (useAuth lê do localStorage quando o App montar)
 * - #error= → guarda o código em sessionStorage pro App mostrar msg amigável
 */
export function handleGoogleAuthReturn(): void {
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const token = params.get('token');
  const error = params.get('error');

  if (token) {
    try {
      storeJwt(token);
    } catch {
      /* localStorage bloqueado — médico cai no login normal */
    }
  } else if (error) {
    try {
      sessionStorage.setItem(GOOGLE_AUTH_ERROR_KEY, error);
    } catch {
      /* sem sessionStorage a msg amigável some, mas o fluxo segue */
    }
  }

  // Limpa fragment + volta pra raiz — o token não fica no histórico.
  window.history.replaceState({}, '', '/');
}

/** Lê e consome (one-shot) o código de erro do login Google, se houver */
export function consumeGoogleAuthError(): string | null {
  try {
    const value = sessionStorage.getItem(GOOGLE_AUTH_ERROR_KEY);
    if (value !== null) sessionStorage.removeItem(GOOGLE_AUTH_ERROR_KEY);
    return value;
  } catch {
    return null;
  }
}

/** Mapeia código do backend → chave i18n (desconhecido → generic) */
export function googleAuthErrorToKey(code: string): string {
  switch (code) {
    case 'denied':
      return 'googleAuth.error.denied';
    case 'invalid_state':
      return 'googleAuth.error.invalidState';
    case 'exchange_failed':
      return 'googleAuth.error.exchangeFailed';
    case 'email_not_verified':
      return 'googleAuth.error.emailNotVerified';
    default:
      return 'googleAuth.error.generic';
  }
}
